/**
 * consoleTrail — 把未處理錯誤收進診斷緩衝(R249 Step 1)
 * =============================================================================
 *
 * 掛兩個 window listener:
 * - `error` — 未 catch 的例外(含 `AppErrorBoundary` 接不到的那四類:事件處理器、
 *   `setTimeout` / `.then` 內的非同步 callback…)
 * - `unhandledrejection` — 沒有 `.catch()` 的 promise。這一類在我們的 code base 特別
 *   值得收:service 呼叫失敗時若呼叫端漏 catch,畫面只是靜靜沒反應,console 以外沒有
 *   任何線索,而使用者的描述就是「按了沒事發生」。
 *
 * ## 刻意**不** patch `console.error`
 *
 * 那樣能多收東西,但 React 在 dev 用 `console.error` 印大量 warning(key prop、
 * `act()` 提示、controlled input 切換…),20 格的環形緩衝會被噪音灌滿,把真正的錯誤
 * 沖掉 —— 收集器把訊號淹掉就失去意義了。
 *
 * 真正需要的那一筆(render 期例外)由 [AppErrorBoundary](../../app/AppErrorBoundary.tsx)
 * 的 `componentDidCatch` **直接呼叫** {@link recordConsoleError} 交進來,連 component
 * stack 一起 —— 比 patch console 再回頭解析字串精準得多。
 */

import { recordConsoleError } from './diagnosticsBuffer';

let installed = false;

/**
 * 裝上 listener。重複呼叫是安全的(HMR / 測試會重跑 module)。
 *
 * 兩個 handler 都**不** `preventDefault`:錯誤照樣印進 console、照樣往上冒泡到任何
 * 既有的處理鏈。這支的職責只有「順手記一份」。
 */
export function installConsoleTrail(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (e: ErrorEvent) => {
    // 資源載入失敗(`<img>` / `<script>` 404)也會觸發 error,但那是 Event 不是
    // ErrorEvent,`e.message` 為空。這類同樣值得記 —— lazy chunk 的 404 正是
    // R225 那隻「畫面白掉」的病根,而它在 console 只有一行看不出所以然的訊息。
    const message = e.message || `resource error: ${describeTarget(e.target)}`;
    recordConsoleError('error', message, e.error instanceof Error ? e.error.stack : null);
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason: unknown = e.reason;
    if (reason instanceof Error) {
      recordConsoleError('unhandledrejection', `${reason.name}: ${reason.message}`, reason.stack);
      return;
    }
    recordConsoleError('unhandledrejection', safeStringify(reason), null);
  });
}

/** `<img src=…>` 這類載入失敗的 target 描述;拿不到就給一個誠實的佔位。 */
function describeTarget(target: EventTarget | null): string {
  if (target === null) return 'unknown';
  const el = target as Partial<HTMLElement> & { src?: string; href?: string };
  const tag = typeof el.tagName === 'string' ? el.tagName.toLowerCase() : 'unknown';
  const url = el.src ?? el.href ?? '';
  // ⚠️ URL 不整條記:簽名素材 URL 帶 query token。只留 origin + pathname 的最後一段。
  return url ? `${tag} ${lastPathSegment(url)}` : tag;
}

function lastPathSegment(url: string): string {
  const withoutQuery = url.split('?', 1)[0] ?? url;
  const parts = withoutQuery.split('/');
  return parts[parts.length - 1] || withoutQuery;
}

function safeStringify(v: unknown): string {
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v) ?? String(v);
  } catch {
    return String(v);
  }
}

/** 測試用:讓下一次 install 重新掛上。 */
export function resetConsoleTrailForTest(): void {
  installed = false;
}
